// PorterIQ Design System Tokens
// Single source of truth for colors, gradients, and styling

// ---------------------------------------------------------------------------
// Glassmorphism Standard - Opacity 0.70 (70% opaque) for better readability
// ---------------------------------------------------------------------------
export const GLASS_OPACITY = 0.70;

// ---------------------------------------------------------------------------
// Accent Colors
// ---------------------------------------------------------------------------
export const ACCENT_COLORS = {
  primary: "#2563eb",   // Blue
  secondary: "#7c3aed", // Purple
  highlight: "#0075FF", // Bright blue — icon backgrounds, stat card accents
};

// ---------------------------------------------------------------------------
// Gradients
// ---------------------------------------------------------------------------
export const GRADIENTS = {
  cardBorder: "linear-gradient(135deg, rgba(37,99,235,0.5), rgba(124,58,237,0.3))",
  accent: "linear-gradient(135deg, #2563eb, #7c3aed)",
  text: "linear-gradient(90deg, #60A5FA, #A78BFA, #9f7aea)",
  engagementBadge: "linear-gradient(135deg, rgba(37,99,235,0.8), rgba(124,58,237,0.6))",
  pageBg: "linear-gradient(135deg, #1A1F37 0%, #111328 100%)",
  chartAccent: "linear-gradient(90deg, #60A5FA, #A78BFA)",
};

// ---------------------------------------------------------------------------
// Glass Card Styles
// ---------------------------------------------------------------------------
export const GLASS_CARD = {
  background: "rgba(255, 255, 255, 0.08)",
  backdropFilter: "blur(16px)",
  borderRadius: "20px",
  padding: "12px",
};

// ---------------------------------------------------------------------------
// Chart Colors - Muted palette for glassmorphism compatibility
// ---------------------------------------------------------------------------
export const CHART_COLORS = [
  "#60A5FA", // Blue 400
  "#A78BFA", // Violet 400
  "#34D399", // Emerald 400
  "#FBBF24", // Amber 400
  "#F472B6", // Pink 400
  "#22D3EE", // Cyan 400
  "#818CF8", // Indigo 400
  "#FB923C", // Orange 400
];

// ---------------------------------------------------------------------------
// Navigation / sidebar (must align with theme breakpoints in assets/theme/base/breakpoints.js)
// ---------------------------------------------------------------------------
export const SIDENAV_LAYOUT = {
  /** Viewport width (px) below which the rail auto-collapses to icons. Was 1280; lowered to lg. */
  miniBreakpointPx: 1024,
};

// ---------------------------------------------------------------------------
// Common Component Styles
// ---------------------------------------------------------------------------
export const COMPONENT_STYLES = {
  iconButton: {
    color: "#fff",
    backgroundColor: "rgba(255, 255, 255, 0.15)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.25)",
    },
  },
  glassIconBox: {
    background: "linear-gradient(135deg, rgba(37,99,235,0.3), rgba(124,58,237,0.2))",
    backdropFilter: "blur(10px)",
    borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.15)",
  },
  chartBackground: "transparent",
};

// ---------------------------------------------------------------------------
// Heatmap Color Schemes
// ---------------------------------------------------------------------------
export const HEATMAP_COLORS = {
  default: {
    low: "rgba(34, 211, 238, 0.8)",      // Cyan
    medium: "rgba(96, 165, 250, 0.85)",   // Blue
    high: "rgba(167, 139, 250, 0.9)",     // Violet
    veryHigh: "rgba(244, 114, 182, 1)",   // Pink
  },
};

// ---------------------------------------------------------------------------
// Dropdown Styles (DateRange, EntitySelector, Table Filters)
// ---------------------------------------------------------------------------
export const DROPDOWN = {
  background: `rgba(26, 31, 55, ${GLASS_OPACITY})`,
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(255, 255, 255, 0.25)",
  borderRadius: "10px",
  itemHover: "rgba(255, 255, 255, 0.15)",
  textColor: "white",
  secondaryText: "rgba(255, 255, 255, 0.8)",
  selectedBg: "rgba(37, 99, 235, 0.5)",
  selectedHover: "rgba(37, 99, 235, 0.6)",
  // Button container styles
  button: {
    height: "38px",
    padding: "8px 12px",
    minWidth: "140px",
    maxWidth: "280px",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  // Fixed label column below lg (matches compact navbar); ellipsis if label is longer than column
  filterLabelColumnWidth: { xs: 84, lg: "auto" },
  /** Same pill width for every filter row: fills row below lg; fixed at lg+ so Sources/Timeframe match */
  filterDropdownButton: {
    flex: { xs: 1, lg: "0 0 173px" },
    width: { xs: "100%", lg: "173px" },
    minWidth: { xs: 0, lg: "173px" },
    maxWidth: { xs: "100%", lg: "173px" },
  },
  // Label styles
  label: {
    variant: "caption",
    fontWeight: "medium",
    color: "white",
  },
  // Text display styles
  text: {
    fontSize: "13px",
    lineHeight: 1,
    fontWeight: "medium",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

// Alias for backwards compatibility
export const FILTER_DROPDOWN = DROPDOWN;

// ---------------------------------------------------------------------------
// Input Styles (for TextField, search inputs in dropdowns)
// ---------------------------------------------------------------------------
export const INPUT = {
  background: "rgba(255, 255, 255, 0.18)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  borderRadius: "8px",
  textColor: "white",
  placeholderColor: "rgba(255, 255, 255, 0.6)",
  focusBorder: "rgba(37, 99, 235, 0.5)",
  focusShadow: "0 0 0 2px rgba(37, 99, 235, 0.2)",
};

// ---------------------------------------------------------------------------
// Pagination Styles
// ---------------------------------------------------------------------------
export const PAGINATION = {
  background: `rgba(30, 37, 93, ${GLASS_OPACITY})`,
  backdropFilter: "blur(16px)",
  backgroundHover: "rgba(55, 64, 140, 0.7)",
  activeBg: "rgba(37, 99, 235, 0.5)", // Blue accent
  textColor: "white",
  borderRadius: "8px",
  border: "1px solid rgba(87, 97, 180, 0.5)",
  inputBackground: `rgba(30, 37, 93, ${GLASS_OPACITY})`,
  inputBorder: "1px solid rgba(87, 97, 180, 0.5)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
};

// ---------------------------------------------------------------------------
// Modal Styles
// ---------------------------------------------------------------------------
export const MODAL = {
  background: `rgba(20, 20, 30, ${GLASS_OPACITY + 0.1})`,
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  borderRadius: "16px",
};

// ---------------------------------------------------------------------------
// Popover Styles (Filter dropdowns, etc.)
// ---------------------------------------------------------------------------
export const POPOVER = {
  background: `rgba(26, 31, 55, ${GLASS_OPACITY})`,
  backdropFilter: "blur(16px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  borderRadius: "12px",
  arrowBackground: `rgba(26, 31, 55, ${GLASS_OPACITY})`,
  arrowBorder: "rgba(255, 255, 255, 0.1)",
};

// ---------------------------------------------------------------------------
// Loading Overlay Styles
// ---------------------------------------------------------------------------
export const LOADING_OVERLAY = {
  background: `rgba(26, 31, 55, 0.75)`, // More opaque for readability
  backdropFilter: "blur(2px)",
  borderRadius: "12px",
  padding: "12px 24px",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
};

// ---------------------------------------------------------------------------
// Modal Position Styles (full-screen centered modal overlay)
// ---------------------------------------------------------------------------
export const MODAL_POSITION_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "1500px",
  height: "85vh",
  maxHeight: "90vh",
  bgcolor: MODAL.background,
  backdropFilter: MODAL.backdropFilter,
  border: `1px solid ${ACCENT_COLORS.primary}`,
  borderRadius: MODAL.borderRadius,
  boxShadow: 24,
  p: 3,
  display: "flex",
  flexDirection: "column",
};

// Alias for backward compatibility
export const modalStyle = MODAL_POSITION_STYLE;

// ---------------------------------------------------------------------------
// PorterTable Styles (new plain-HTML table component)
// ---------------------------------------------------------------------------
export const PORTER_TABLE = {
  cell: {
    fontFamily: '"Sora", "Helvetica", "Arial", sans-serif',
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.4,
    color: "rgba(255,255,255,0.85)",
    padding: "7px 14px",
  },
  header: {
    background: "linear-gradient(135deg, rgba(37,99,235,0.5), rgba(124,58,237,0.4))",
    backdropFilter: "blur(10px)",
    fontSize: "10px",
    fontWeight: 600,
    color: "rgba(255,255,255,1)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: "8px 14px",
  },
  row: {
    borderColor: "rgba(255,255,255,0.08)",
    hoverBg: "rgba(255,255,255,0.03)",
  },
  scrollbar: {
    width: "8px",
    height: "8px",
    thumbColor: "rgba(255,255,255,0.2)",
    thumbRadius: "4px",
  },
  defaultMaxHeight: "380px",
};

